import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, UserRound, Coins, Gift, Timer } from "lucide-react";
import { api } from "../lib/api";
import { SITE } from "../mock";
import Reveal from "../components/site/Reveal";

const FRAMES = {
  1: {
    img: "/img/lb_frame_1.png",
    width: "w-64 sm:w-72 lg:w-80",
    order: "order-1 sm:order-2",
    wager: "text-[#ffd94a]",
    badge: "bg-gradient-to-b from-[#ffe887] to-[#e2b422] text-[#6b4a0e]",
    plaqueText: "text-[#4a3510]",
    avatar: "top-[13.9%] w-[36%]",
    plaque: "left-[26%] right-[26%] bottom-[9.5%] h-[8%]",
  },
  2: {
    img: "/img/lb_frame_2.png",
    width: "w-52 sm:w-56 lg:w-64",
    order: "order-2 sm:order-1",
    wager: "text-[#e3edf8]",
    badge: "bg-gradient-to-b from-[#f2f7fd] to-[#a6bcd4] text-[#39475a]",
    plaqueText: "text-[#39475a]",
    avatar: "top-[13.9%] w-[34%]",
    plaque: "left-[23%] right-[23%] bottom-[10%] h-[9.2%]",
  },
  3: {
    img: "/img/lb_frame_3.png",
    width: "w-52 sm:w-56 lg:w-64",
    order: "order-3 sm:order-3",
    wager: "text-[#f2b078]",
    badge: "bg-gradient-to-b from-[#f6cda4] to-[#b97a3c] text-[#4a2c14]",
    plaqueText: "text-[#3a2210]",
    avatar: "top-[15%] w-[42%]",
    plaque: "left-[24%] right-[24%] bottom-[9.7%] h-[10.5%]",
  },
};

const LbFrameCard = ({ rank, prize }) => {
  const f = FRAMES[rank];
  return (
    <div data-testid={`lb-frame-card-${rank}`} className={`relative ${f.width} ${f.order} shrink-0`}>
      <img
        src={f.img}
        alt={`Rank ${rank} frame`}
        className="w-full h-auto select-none pointer-events-none drop-shadow-[0_22px_40px_rgba(2,10,26,0.55)]"
      />
      {/* medallion: avatar + rank badge */}
      <div className={`absolute left-1/2 -translate-x-1/2 ${f.avatar} aspect-square flex items-center justify-center`}>
        <span className="lb-avatar !w-full !h-full !border-white/25">
          <UserRound className="w-[38%] h-[38%]" strokeWidth={2} />
        </span>
        <span
          className={`absolute -bottom-1 -right-1 w-[34%] h-[34%] rounded-full flex items-center justify-center font-display font-bold text-[0.95rem] sm:text-[1.05rem] border-2 border-black/20 shadow-[0_4px_10px_rgba(0,0,0,0.4)] ${f.badge}`}
        >
          {rank}
        </span>
      </div>
      {/* username / wagered */}
      <div className="absolute left-[16%] right-[16%] top-[42%] bottom-[24%] flex flex-col items-center justify-center text-center gap-1 overflow-hidden">
        <p className="text-white font-display font-bold text-[1.15rem] sm:text-[1.35rem] leading-tight truncate max-w-full">
          —
        </p>
        <p className={`${f.wager} font-display font-bold text-[1.3rem] sm:text-[1.6rem] leading-tight`}>—</p>
        <p className="text-[#8fa8c4] text-[0.62rem] sm:text-[0.68rem] font-bold tracking-[0.34em] uppercase mt-0.5">
          Wagered
        </p>
      </div>
      {/* prize plaque */}
      <div className={`absolute ${f.plaque} flex items-center justify-center`}>
        <span
          data-testid={`lb-frame-prize-${rank}`}
          className={`${f.plaqueText} font-display font-bold text-[0.95rem] sm:text-[1.1rem] leading-none truncate`}
        >
          {prize || "\u2014"}
        </span>
      </div>
    </div>
  );
};

const PrizeChip = ({ prize, gold = true }) =>
  prize ? (
    <span className={gold ? "prize-chip" : "prize-chip prize-chip-ghost"}>
      <Gift className="w-3 h-3" strokeWidth={2.8} />
      {prize}
    </span>
  ) : (
    <span className="prize-chip prize-chip-ghost">—</span>
  );

const LbRow = ({ rank, prize }) => (
  <div className="lb-row" data-testid={`lb-row-${rank}`}>
    <span className="lb-row-rank">#{rank}</span>
    <span className="lb-avatar lb-avatar-sm">
      <UserRound className="w-3.5 h-3.5" strokeWidth={2.2} />
    </span>
    <span className="text-white font-display font-bold text-[0.95rem] flex-1 truncate">—</span>
    <span className="hidden sm:flex items-center gap-1.5 text-[#9fc5e8] text-[0.62rem] font-bold tracking-[0.2em] uppercase">
      <Coins className="w-3 h-3" />
      Wagered
    </span>
    <span className="text-[#d7eafb] font-display font-bold text-[0.9rem] min-w-[52px] text-right">—</span>
    <PrizeChip prize={prize} />
  </div>
);

const pad = (n) => String(n).padStart(2, "0");

const getTimeLeft = () => {
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const diff = Math.max(0, end.getTime() - now.getTime());
  return {
    Days: Math.floor(diff / 86400000),
    Hours: Math.floor(diff / 3600000) % 24,
    Mins: Math.floor(diff / 60000) % 60,
    Secs: Math.floor(diff / 1000) % 60,
  };
};

const RaceCountdown = () => {
  const [t, setT] = useState(getTimeLeft());
  useEffect(() => {
    const id = setInterval(() => setT(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="lb-race-countdown" className="flex flex-col items-center gap-2.5">
      <span className="text-[#9fc5e8] text-[0.68rem] font-bold tracking-[0.3em] uppercase flex items-center gap-1.5">
        <Timer className="w-3.5 h-3.5" strokeWidth={2.4} />
        Race ends in
      </span>
      <div className="flex items-center gap-2 sm:gap-3">
        {Object.entries(t).map(([label, val]) => (
          <div
            key={label}
            className="w-16 sm:w-20 py-2.5 rounded-2xl bg-[rgba(6,32,66,0.45)] border border-white/15 backdrop-blur-md text-center shadow-[0_10px_24px_rgba(2,10,26,0.35)]"
          >
            <p className="text-white font-display font-bold text-[1.35rem] sm:text-[1.6rem] leading-none">{pad(val)}</p>
            <p className="text-[#8fb4d8] text-[0.55rem] font-bold tracking-[0.24em] uppercase mt-1">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const PrizePool = ({ total, label }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!total || !ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const dur = 1800;
          const tick = (now) => {
            const p = Math.min(1, (now - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(total * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [total]);

  return (
    <div ref={ref} data-testid="lb-prize-pool" className="flex flex-col items-center gap-1.5">
      <span className="text-[#9fc5e8] text-[0.72rem] sm:text-[0.8rem] font-bold tracking-[0.32em] uppercase">
        {label}
      </span>
      <span className="font-display font-bold leading-none text-[3rem] sm:text-[4.2rem] lg:text-[4.8rem] text-transparent bg-clip-text bg-gradient-to-b from-[#fff3b0] via-[#ffd94a] to-[#e2a812] drop-shadow-[0_12px_30px_rgba(245,207,64,0.4)]">
        ${display.toLocaleString()}
      </span>
    </div>
  );
};

const Leaderboard = () => {
  const [prizes, setPrizes] = useState(Array(10).fill(""));
  const [poolLabel, setPoolLabel] = useState("Total Prize Pool");

  useEffect(() => {
    api
      .get("/leaderboard")
      .then((res) => {
        setPrizes(res.data.prizes || []);
        if (res.data.pool_label) setPoolLabel(res.data.pool_label);
      })
      .catch(() => {});
  }, []);

  const prizeTotal = prizes.reduce(
    (sum, p) => sum + (parseFloat(String(p).replace(/[^0-9.]/g, "")) || 0),
    0
  );

  return (
    <main className="relative z-10 min-h-[100svh] pt-36 sm:pt-40 pb-24">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center flex flex-col items-center">
            <PrizePool total={prizeTotal} label={poolLabel} />
            <div className="mt-7 flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
              <img
                src={SITE.partner.logo}
                alt={SITE.partner.name}
                data-testid="lb-partner-logo"
                className="h-9 sm:h-12 lg:h-14 w-auto drop-shadow-[0_8px_18px_rgba(2,10,26,0.45)]"
              />
              <h1 className="heading-3d text-[2.4rem] sm:text-[3.4rem] lg:text-[4rem]">Leaderboard</h1>
            </div>
            <p className="mt-4 max-w-xl text-[0.98rem] sm:text-[1.02rem] leading-relaxed text-[#bcd9f2]">
              Monthly wager race on {SITE.partner.name} under code “{SITE.code}”. The deeper you wager, the higher you rank — top 10 split the loot.
            </p>
          </div>
        </Reveal>

        {/* Countdown above the podium */}
        <Reveal delay={80}>
          <div className="mt-12 flex justify-center">
            <RaceCountdown />
          </div>
        </Reveal>

        {/* Top 3 champion frames */}
        <Reveal delay={140}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-6 lg:gap-10">
            <LbFrameCard rank={2} prize={prizes[1]} />
            <LbFrameCard rank={1} prize={prizes[0]} />
            <LbFrameCard rank={3} prize={prizes[2]} />
          </div>
        </Reveal>

        {/* Sign up below the podium */}
        <Reveal delay={200}>
          <div className="mt-14 flex flex-col items-center gap-3 text-center">
            <a
              href={SITE.partner.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lb-signup-btn"
              className="btn-pill btn-yellow min-w-[230px]"
            >
              Sign Up
              <ArrowRight className="w-[18px] h-[18px]" strokeWidth={2.6} />
            </a>
            <p className="text-[#9fc5e8] text-[0.9rem]">
              Wager on {SITE.partner.name} with code “{SITE.code}” to enter the race.
            </p>
          </div>
        </Reveal>

        {/* Ranks 4-10 line by line */}
        <Reveal delay={240}>
          <div className="relative mt-14 max-w-[820px] mx-auto">
            <div className="space-y-2.5" data-testid="lb-rows-list">
              {[4, 5, 6, 7, 8, 9, 10].map((rank) => (
                <LbRow key={rank} rank={rank} prize={prizes[rank - 1]} />
              ))}
            </div>
            <img
              src="/img/lb_sea_decor_left.png"
              alt=""
              aria-hidden="true"
              className="hidden sm:block absolute -left-12 -bottom-7 w-32 z-20 select-none pointer-events-none drop-shadow-[0_12px_20px_rgba(2,10,26,0.5)]"
            />
            <img
              src="/img/lb_sea_decor_right.png"
              alt=""
              aria-hidden="true"
              className="hidden sm:block absolute -right-24 -bottom-5 w-28 z-20 select-none pointer-events-none drop-shadow-[0_12px_20px_rgba(2,10,26,0.5)]"
            />
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default Leaderboard;
